import React from 'react';
import {
  X,
  Navigation,
  MapPin,
  Clock,
  IndianRupee,
  Star,
  Phone,
  MessageCircle,
  User,
  Car,
  Calendar,
  CreditCard,
  FileText
} from 'lucide-react';
import { Button } from '../ui/Button/Button';
import { Badge } from '../ui/Badge';
import { TRIP_STATUS } from '../../store/tripSlice';

/**
 * TripDetailsModal Component
 * 
 * A comprehensive modal for displaying detailed trip information
 * including route, participants, timeline, and payment details.
 */
export function TripDetailsModal({ trip, isOpen, onClose, onCompleteTrip, onCancelTrip }) {
  if (!isOpen || !trip) return null;

  const getStatusBadge = (status) => {
    const statusConfig = {
      [TRIP_STATUS.COMPLETED]: { variant: 'success', label: 'Completed' },
      [TRIP_STATUS.CANCELLED]: { variant: 'danger', label: 'Cancelled' },
      [TRIP_STATUS.ONGOING]: { variant: 'info', label: 'In Progress' },
      [TRIP_STATUS.ASSIGNED]: { variant: 'warning', label: 'Assigned' },
      [TRIP_STATUS.REQUESTED]: { variant: 'secondary', label: 'Requested' }
    };
    
    const config = statusConfig[status] || { variant: 'secondary', label: status };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (minutes) => {
    if (!minutes) return 'N/A';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const StarRating = ({ rating }) => {
    if (!rating) return <span className="text-gray-400">No rating</span>;
    
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${
              star <= rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
            }`}
          />
        ))}
        <span className="ml-2 text-sm font-medium">{rating}.0</span>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block w-full max-w-4xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Navigation className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  Trip #{trip.id.slice(-6)}
                </h2>
                <p className="text-gray-600">
                  {formatDateTime(trip.requestedAt)}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {getStatusBadge(trip.status)}
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Route Information */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <MapPin className="w-5 h-5 mr-2 text-green-600" />
                  Route Details
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-4">
                    <div className="w-4 h-4 rounded-full bg-green-500 mt-1 flex-shrink-0"></div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{trip.pickup.name}</p>
                      <p className="text-sm text-gray-600">{trip.pickup.address}</p>
                      {trip.pickup.coordinates && (
                        <p className="text-xs text-gray-500">
                          {trip.pickup.coordinates.lat.toFixed(4)}, {trip.pickup.coordinates.lng.toFixed(4)}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-4 h-4 rounded-full bg-red-500 mt-1 flex-shrink-0"></div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{trip.dropoff.name}</p>
                      <p className="text-sm text-gray-600">{trip.dropoff.address}</p>
                      {trip.dropoff.coordinates && (
                        <p className="text-xs text-gray-500">
                          {trip.dropoff.coordinates.lat.toFixed(4)}, {trip.dropoff.coordinates.lng.toFixed(4)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-200">
                  <div>
                    <p className="text-sm text-gray-600">Distance</p>
                    <p className="font-medium">{trip.distance} km</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Duration</p>
                    <p className="font-medium">
                      {trip.actualDuration 
                        ? formatDuration(trip.actualDuration) 
                        : formatDuration(trip.estimatedDuration)
                      }
                    </p>
                  </div>
                </div>
              </div>

              {/* Participants */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <User className="w-5 h-5 mr-2 text-blue-600" />
                  Participants
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Passenger */}
                  <div className="bg-white rounded-lg p-4">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{trip.passenger.name}</p>
                        <p className="text-sm text-gray-600">Passenger</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Phone className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">{trip.passenger.phone}</span>
                      </div>
                      {trip.rating?.passenger && (
                        <div className="flex items-center space-x-2">
                          <Star className="w-4 h-4 text-yellow-400" />
                          <span className="text-sm text-gray-600">Rated: {trip.rating.passenger}/5</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Driver */}
                  {trip.driver ? (
                    <div className="bg-white rounded-lg p-4">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                          <Car className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{trip.driver.name}</p>
                          <p className="text-sm text-gray-600">Driver</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <Phone className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-600">{trip.driver.phone}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Car className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-600">{trip.driver.vehicle}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Star className="w-4 h-4 text-yellow-400" />
                          <span className="text-sm text-gray-600">Rating: {trip.driver.rating}/5</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-white rounded-lg p-4 flex items-center justify-center">
                      <p className="text-gray-500">No driver assigned</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Special Notes */}
              {trip.notes && (
                <div className="bg-blue-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <FileText className="w-5 h-5 mr-2 text-blue-600" />
                    Special Notes
                  </h3>
                  <p className="text-gray-700">{trip.notes}</p>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Trip Timeline */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Clock className="w-5 h-5 mr-2 text-purple-600" />
                  Timeline
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                    <div>
                      <p className="text-sm font-medium">Requested</p>
                      <p className="text-xs text-gray-600">{formatDateTime(trip.requestedAt)}</p>
                    </div>
                  </div>
                  {trip.assignedAt && (
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 rounded-full bg-yellow-400"></div>
                      <div>
                        <p className="text-sm font-medium">Assigned</p>
                        <p className="text-xs text-gray-600">{formatDateTime(trip.assignedAt)}</p>
                      </div>
                    </div>
                  )}
                  {trip.startedAt && (
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 rounded-full bg-blue-400"></div>
                      <div>
                        <p className="text-sm font-medium">Started</p>
                        <p className="text-xs text-gray-600">{formatDateTime(trip.startedAt)}</p>
                      </div>
                    </div>
                  )}
                  {trip.completedAt && (
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 rounded-full bg-green-400"></div>
                      <div>
                        <p className="text-sm font-medium">Completed</p>
                        <p className="text-xs text-gray-600">{formatDateTime(trip.completedAt)}</p>
                      </div>
                    </div>
                  )}
                  {trip.cancelledAt && (
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 rounded-full bg-red-400"></div>
                      <div>
                        <p className="text-sm font-medium">Cancelled</p>
                        <p className="text-xs text-gray-600">{formatDateTime(trip.cancelledAt)}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Payment Information */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <IndianRupee className="w-5 h-5 mr-2 text-green-600" />
                  Payment
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Base Fare</span>
                    <span className="font-medium">${trip.fare.base.toFixed(2)}</span>
                  </div>
                  {trip.fare.surge > 1 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Surge ({trip.fare.surge}x)</span>
                      <span className="font-medium text-orange-600">
                        +${((trip.fare.total - trip.fare.base)).toFixed(2)}
                      </span>
                    </div>
                  )}
                  <div className="border-t border-gray-200 pt-3">
                    <div className="flex justify-between">
                      <span className="font-semibold">Total</span>
                      <span className="font-bold text-lg">${trip.fare.total}</span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 mt-3">
                    <CreditCard className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600 capitalize">
                      {trip.payment.method} • {trip.payment.status}
                    </span>
                  </div>
                </div>
              </div>

              {/* Ratings */}
              {(trip.rating?.passenger || trip.rating?.driver) && (
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Star className="w-5 h-5 mr-2 text-yellow-600" />
                    Ratings
                  </h3>
                  <div className="space-y-3">
                    {trip.rating?.passenger && (
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Passenger Rating</p>
                        <StarRating rating={trip.rating.passenger} />
                      </div>
                    )}
                    {trip.rating?.driver && (
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Driver Rating</p>
                        <StarRating rating={trip.rating.driver} />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="space-y-3">
                {trip.status === TRIP_STATUS.ONGOING && onCompleteTrip && (
                  <Button
                    variant="primary"
                    onClick={() => onCompleteTrip(trip.id)}
                    className="w-full"
                  >
                    Complete Trip
                  </Button>
                )}
                {[TRIP_STATUS.REQUESTED, TRIP_STATUS.ASSIGNED].includes(trip.status) && onCancelTrip && (
                  <Button
                    variant="outline"
                    onClick={() => onCancelTrip(trip.id)}
                    className="w-full"
                  >
                    Cancel Trip
                  </Button>
                )}
                {[TRIP_STATUS.ONGOING, TRIP_STATUS.ASSIGNED].includes(trip.status) && (
                  <div className="grid grid-cols-2 gap-2">
                    <Button variant="outline" size="sm">
                      <Phone className="w-4 h-4 mr-2" />
                      Call
                    </Button>
                    <Button variant="outline" size="sm">
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Message
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
